/**
 * Parser for the MVMS Syndication Upfront Estimates xlsx format.
 *
 * Source file shape (confirmed against the 26/27 season file):
 *   Row 1   — section headers spanning groups (cols G/K/O/S)
 *   Row 2   — column headers including the 4 quarter labels
 *   Row 3+  — data, one row per (program, day, demo)
 *
 * Column layout:
 *   A  Selling Title        (program)
 *   B  As of                (date)
 *   C  Day                  (M-F / M-Su / Wknd)
 *   D  Universe             (per-demo population in 000s)
 *   E  Demo                 (HH, A2+, A18+, …)
 *   F  VPVH                 (single value, shared across quarters)
 *   G…  Sales Estimate - IMPS (000)   — N quarters
 *   …   Projected - IMPS (000)        — N quarters (stored for reference)
 *   …   Sales Estimate - Rating       — N quarters
 *   …   Projected - Rating            — N quarters (stored for reference)
 *
 * The quarter count N is DISCOVERED, not assumed. It used to be hardcoded
 * to 4, which is right for a normal 26/27 file (4Q'26, 1Q'27, 2Q'27, 3Q'27)
 * but wrong for the extended file MVMS ships later in the season carrying
 * 4Q'27 as a fifth quarter. See `discoverQuarters`.
 *
 * Section-header comparison is hyphen-tolerant — see `normalizeForLabel`.
 * MVMS ships both hyphenated ("Sales Estimate - IMPS (000)") and non-
 * hyphenated variants across files; the parser accepts either.
 *
 * Output: an EstimateVersion-shaped object minus the id/uploadedAt/uploadedBy
 * fields which the storage layer assigns. The `estimateDb` it produces is
 * key-compatible with the legacy hardcoded ESTIMATE_DB so MediaPlanBuilder
 * consumes it without changes.
 */

import * as XLSX from 'xlsx-js-style';
import { ESTIMATE_NAME_MAP } from '../media-plan-builder/data/constants';
import { EstimateEntry } from '../media-plan-builder/data/estimateDb';
import { EstimateVersion, ParseResult, QuarterLabel, normalizeProgramName } from './types';

const SHEET_NAME_PRIMARY = 'Sheet1'; // observed in current files; fallback to first sheet

/**
 * Resolved column layout — populated at parse time by scanning the
 * header rows by NAME rather than fixed position. This lets MVMS reorder
 * or insert columns without silently corrupting our data: if a required
 * header is missing the parser fails loudly with a precise error message.
 *
 * Scalar columns come from the per-column header row. Section starts
 * (SE_IMPS / PROJ_IMPS / SE_RTG / PROJ_RTG) come from the section header
 * row above it; each section is followed by N contiguous quarter columns,
 * where N is discovered from the file rather than assumed.
 */
interface ColumnLayout {
  program: number;
  asOf: number;
  day: number;
  universe: number;
  demo: number;
  vpvh: number;
  seImpsStart: number;
  projImpsStart: number;
  seRtgStart: number;
  projRtgStart: number;
}

const SECTION_HEADER_ROW = 0;
const COLUMN_HEADER_ROW = 1;
const DATA_START_ROW = 2; // 0-based: row 0 = section, row 1 = headers, row 2 = data

/**
 * Required scalar column headers as they appear in the per-column header
 * row. Compared after .trim() + case-insensitive — MVMS sometimes ships
 * the same file with slightly different casing.
 */
const REQUIRED_SCALARS: Array<{ key: keyof Omit<ColumnLayout,
  'seImpsStart' | 'projImpsStart' | 'seRtgStart' | 'projRtgStart'>;
  header: string }> = [
  { key: 'program',  header: 'Selling Title' },
  { key: 'asOf',     header: 'As of' },
  { key: 'day',      header: 'Day' },
  { key: 'universe', header: 'Universe' },
  { key: 'demo',     header: 'Demo' },
  { key: 'vpvh',     header: 'VPVH' },
];

/**
 * Required section labels as they appear in the section-header row.
 * Compared case-insensitively after .trim(). Matching is `startsWith`
 * because the actual cells sometimes carry extra suffix like "(000)".
 */
const REQUIRED_SECTIONS: Array<{ key: 'seImpsStart' | 'projImpsStart' | 'seRtgStart' | 'projRtgStart';
  label: string }> = [
  { key: 'seImpsStart',   label: 'Sales Estimate IMPS' },
  { key: 'projImpsStart', label: 'Projected IMPS' },
  { key: 'seRtgStart',    label: 'Sales Estimate Rating' },
  { key: 'projRtgStart',  label: 'Projected Rating' },
];

/** 0-based column index → "A", "B", ..., "AA". Used for error messages. */
function colLetter(index: number): string {
  let s = '';
  let n = index;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

/** Case-/whitespace-insensitive cell equality. */
function cellEquals(cell: unknown, expected: string): boolean {
  return String(cell ?? '').trim().toLowerCase() === expected.toLowerCase();
}
/**
 * Normalize a label for section-header comparison. MVMS reliably ships
 * the section-header row with a hyphen between the section noun and its
 * metric (e.g. `Sales Estimate - IMPS (000)`), but a prior version of
 * this parser was written against a hyphen-less variant. Normalizing
 * both sides — lowercase, drop hyphens/dashes, collapse whitespace —
 * keeps the comparison semantic instead of typographic.
 */
function normalizeForLabel(s: string): string {
  return s
    .toLowerCase()
    .replace(/[-–—]/g, ' ')   // ASCII hyphen + en/em dashes
    .replace(/\s+/g, ' ')
    .trim();
}
function cellStartsWith(cell: unknown, expected: string): boolean {
  const cellNorm = normalizeForLabel(String(cell ?? ''));
  const expNorm  = normalizeForLabel(expected);
  return cellNorm.startsWith(expNorm);
}

/**
 * Scan the section header row + column header row to resolve the layout
 * by name. Returns { layout, errors }; if errors is non-empty the layout
 * is not usable.
 */
function resolveColumnLayout(
  sectionRow: unknown[],
  headerRow: unknown[],
): { layout: ColumnLayout | null; errors: string[] } {
  const errors: string[] = [];
  const partial: Partial<ColumnLayout> = {};

  for (const { key, header } of REQUIRED_SCALARS) {
    const idx = headerRow.findIndex(c => cellEquals(c, header));
    if (idx < 0) {
      errors.push(`Required column header "${header}" not found in row ${COLUMN_HEADER_ROW + 1}.`);
    } else {
      partial[key] = idx;
    }
  }

  for (const { key, label } of REQUIRED_SECTIONS) {
    const idx = sectionRow.findIndex(c => cellStartsWith(c, label));
    if (idx < 0) {
      errors.push(`Required section header "${label}" not found in row ${SECTION_HEADER_ROW + 1}.`);
    } else {
      partial[key] = idx;
    }
  }

  if (errors.length) return { layout: null, errors };
  return { layout: partial as ColumnLayout, errors: [] };
}

/** Quarter label parser. "4Q'26" → { quarter: "Q4", year: 2026 }. */
function parseQuarterLabel(label: string): QuarterLabel | null {
  const m = /^\s*([1-4])Q'(\d{2})\s*$/.exec(label);
  if (!m) return null;
  const qNum = m[1];
  const yy = parseInt(m[2], 10);
  // 2-digit year heuristic: 00-79 → 2000s, 80-99 → 1900s. Plenty of headroom.
  const year = yy < 80 ? 2000 + yy : 1900 + yy;
  return {
    label: label.trim(),
    quarter: `Q${qNum}`,
    year,
    key: `${qNum}Q${yy.toString().padStart(2, '0')}`,
  };
}

/**
 * The quarter that chronologically follows `q`. Q1→Q2→Q3→Q4→Q1-of-next-year.
 * Used only to validate that a file's quarter headers run in an unbroken
 * sequence.
 */
function expectedNextQuarter(q: QuarterLabel): { quarter: string; year: number } {
  const n = parseInt(q.quarter.slice(1), 10);
  return n === 4
    ? { quarter: 'Q1', year: q.year + 1 }
    : { quarter: `Q${n + 1}`, year: q.year };
}

/**
 * Discover how many quarter columns this file carries.
 *
 * Previously this was a hardcoded `QUARTER_COUNT = 4`, which made a
 * 5-quarter file (the extended 26/27 that adds 4Q'27) unparseable. Rather
 * than swap one magic number for another, count the consecutive columns
 * after the Sales Estimate IMPS section header that actually parse as
 * quarter labels, bounded by where the next section begins.
 *
 * Counting real labels rather than differencing the section starts means a
 * spacer column between sections can't inflate the count — we only ever
 * accept columns we successfully understood.
 */
function discoverQuarters(
  headerRow: unknown[],
  startCol: number,
  boundCol: number,
): QuarterLabel[] {
  const out: QuarterLabel[] = [];
  const limit = Math.min(boundCol, headerRow.length);
  for (let i = startCol; i < limit; i++) {
    const ql = parseQuarterLabel(String(headerRow[i] ?? '').trim());
    if (!ql) break;
    out.push(ql);
  }
  return out;
}

/** Excel serial date / Date / string → ISO YYYY-MM-DD. */
function toIsoDate(v: unknown): string | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) {
    const y = v.getUTCFullYear();
    const m = String(v.getUTCMonth() + 1).padStart(2, '0');
    const d = String(v.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof v === 'number') {
    // Excel serial (1900 epoch). SheetJS usually converts to Date when
    // cellDates is true, but be defensive.
    const epoch = Date.UTC(1899, 11, 30);
    const ms = v * 86400 * 1000;
    return toIsoDate(new Date(epoch + ms));
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return toIsoDate(d);
  }
  return null;
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isNaN(n) ? null : n;
}

/**
 * Build one EstimateEntry from the demo row's quarter cell and the
 * matching HH row's quarter cell. Mirrors the legacy schema:
 *   j = HH RTG, m = HH 000s, p = VPVH, x = Demo RTG, t = Demo 000s
 *
 * `null` cells become 0 — the legacy file did the same and downstream
 * math treats 0 as "no data" without exploding.
 */
function buildEntry(args: {
  demoRtg: number | null;
  demoImps: number | null;
  vpvh: number | null;
  hhRtg: number | null;
  hhImps: number | null;
}): EstimateEntry {
  return {
    j: args.hhRtg ?? 0,
    m: args.hhImps ?? 0,
    p: args.vpvh ?? 0,
    x: args.demoRtg ?? 0,
    t: args.demoImps ?? 0,
  };
}

/**
 * Parse a File (or ArrayBuffer) of the new MVMS Syndication Upfront
 * Estimates xlsx. Returns either a populated EstimateVersion-shape
 * object or a list of human-readable errors for the upload UI to show.
 */
export async function parseEstimateFile(
  input: File | ArrayBuffer,
): Promise<ParseResult> {
  const errors: string[] = [];

  let buf: ArrayBuffer;
  let fileName = 'estimate-upload.xlsx';
  if (input instanceof ArrayBuffer) {
    buf = input;
  } else {
    fileName = input.name;
    buf = await input.arrayBuffer();
  }

  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(buf, { type: 'array', cellDates: true });
  } catch (e) {
    return { ok: false, errors: [`Could not read xlsx: ${(e as Error).message}`] };
  }

  const sheetName = wb.SheetNames.includes(SHEET_NAME_PRIMARY)
    ? SHEET_NAME_PRIMARY
    : wb.SheetNames[0];
  if (!sheetName) return { ok: false, errors: ['Workbook has no sheets'] };
  const ws = wb.Sheets[sheetName];
  if (!ws) return { ok: false, errors: [`Sheet "${sheetName}" not found`] };

  // Read as 2D array with empty cells preserved as ''.
  const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: true,
  });

  // ─── Resolve column layout by header name ────────────────────────────
  if (rows.length < 3) {
    return { ok: false, errors: ['File is empty or has no data rows'] };
  }
  const sectionRow = rows[SECTION_HEADER_ROW] as unknown[];
  const headerRow  = rows[COLUMN_HEADER_ROW]  as unknown[];

  const layoutResolution = resolveColumnLayout(sectionRow, headerRow);
  if (layoutResolution.layout == null) {
    return { ok: false, errors: layoutResolution.errors };
  }
  const col = layoutResolution.layout;

  // ─── Discover the quarter labels under the SE IMPS section ────────────
  // Count is derived from the file, not assumed — a normal 26/27 file has
  // 4, the extended one that adds 4Q'27 has 5.
  const quarters = discoverQuarters(headerRow, col.seImpsStart, col.projImpsStart);
  const quarterCount = quarters.length;

  if (quarterCount === 0) {
    const raw = String(headerRow[col.seImpsStart] ?? '').trim();
    return {
      ok: false,
      errors: [
        `No quarter columns found under "Sales Estimate IMPS". Expected a ` +
        `header like "4Q'26" in col ${colLetter(col.seImpsStart)}, found "${raw}".`,
      ],
    };
  }

  // Quarters must run in an unbroken chronological sequence. This replaces
  // the strength the old fixed count used to provide: with the count now
  // discovered, a stray or mis-ordered column would otherwise be accepted
  // silently. Checking the sequence is a stronger guard anyway — it would
  // have caught a scrambled 4-quarter file too.
  for (let i = 1; i < quarterCount; i++) {
    const exp = expectedNextQuarter(quarters[i - 1]);
    if (quarters[i].quarter !== exp.quarter || quarters[i].year !== exp.year) {
      errors.push(
        `Quarter columns are not sequential: "${quarters[i - 1].label}" ` +
        `(col ${colLetter(col.seImpsStart + i - 1)}) is followed by ` +
        `"${quarters[i].label}" (col ${colLetter(col.seImpsStart + i)}).`,
      );
    }
  }

  // Verify the other 3 quarter ranges match the IMPS labels exactly.
  const checkRange = (start: number, label: string) => {
    for (let i = 0; i < quarterCount; i++) {
      const raw = String(headerRow[start + i] ?? '').trim();
      const ql = quarters[i];
      if (ql && raw !== ql.label) {
        errors.push(`${label} quarter in col ${colLetter(start + i)} ("${raw}") doesn't match Sales Estimate IMPS quarter "${ql.label}"`);
      }
    }
  };
  checkRange(col.projImpsStart, 'Projected IMPS');
  checkRange(col.seRtgStart, 'Sales Estimate Rating');
  checkRange(col.projRtgStart, 'Projected Rating');

  // Section overlap guard: every section block must end before the next
  // begins. If MVMS adds a column inside a section, the resolved starts
  // will be too close together and we'd silently read across boundaries.
  // The expected width is now the discovered count rather than a constant,
  // so this still catches a section that is too narrow for its own labels.
  const sectionStarts = [col.seImpsStart, col.projImpsStart, col.seRtgStart, col.projRtgStart];
  for (let i = 0; i < sectionStarts.length - 1; i++) {
    if (sectionStarts[i + 1] - sectionStarts[i] < quarterCount) {
      errors.push(
        `Section blocks overlap — expected ${quarterCount} quarter columns between ` +
        `"${REQUIRED_SECTIONS[i].label}" (col ${colLetter(sectionStarts[i])}) and ` +
        `"${REQUIRED_SECTIONS[i + 1].label}" (col ${colLetter(sectionStarts[i + 1])}).`,
      );
    }
  }

  if (errors.length) return { ok: false, errors };

  // ─── First pass: index HH rows by (program, day) ──────────────────────
  // We need them when assembling entries for non-HH demo rows so the
  // legacy `j`/`m` fields (HH RTG / HH 000s) can be populated.
  type HHRow = {
    rtgs: (number | null)[];
    imps: (number | null)[];
    projRtgs: (number | null)[];
    projImps: (number | null)[];
  };
  const hhIndex = new Map<string, HHRow>();

  // Collect every as-of value in col B. Some programs in a refresh batch
  // don't get updated (analyst skipped them), so their row still carries
  // the older as-of. That's expected — we keep those rows and stamp the
  // version's overall as-of with the MAX (most recent) of the bunch.
  const seenAsOf = new Set<string>();

  for (let r = DATA_START_ROW; r < rows.length; r++) {
    const row = rows[r] as unknown[];
    if (!row || row.length === 0) continue;
    const program = String(row[col.program] ?? '').trim();
    const day = String(row[col.day] ?? '').trim();
    const demo = String(row[col.demo] ?? '').trim();
    if (!program || !day || !demo) continue;

    const iso = toIsoDate(row[col.asOf]);
    if (iso) seenAsOf.add(iso);

    if (demo === 'HH') {
      const rtgs: (number | null)[] = [];
      const imps: (number | null)[] = [];
      const projRtgs: (number | null)[] = [];
      const projImps: (number | null)[] = [];
      for (let q = 0; q < quarterCount; q++) {
        imps.push(numOrNull(row[col.seImpsStart + q]));
        rtgs.push(numOrNull(row[col.seRtgStart + q]));
        projImps.push(numOrNull(row[col.projImpsStart + q]));
        projRtgs.push(numOrNull(row[col.projRtgStart + q]));
      }
      hhIndex.set(`${program}|${day}`, { rtgs, imps, projRtgs, projImps });
    }
  }

  if (seenAsOf.size === 0) {
    return { ok: false, errors: ['No valid "As of" date found in column B'] };
  }
  // Use the most recent as-of across all rows. Older rows simply weren't
  // refreshed in this batch — they're still valid data for unrefreshed
  // programs, and we want them included.
  const dates: string[] = [];
  seenAsOf.forEach(d => dates.push(d));
  dates.sort();
  const asOfDate = dates[dates.length - 1];

  // ─── Second pass: build estimateDb + projectedDb ──────────────────────
  const estimateDb: Record<string, EstimateEntry> = {};
  const projectedEstimateDb: Record<string, EstimateEntry> = {};
  const universeData: Record<string, number> = {};
  // Case- and whitespace-insensitive program name index, populated as
  // we walk rows. Lets consumers look up estimates even when SF data
  // has casing/whitespace drift from the workbook.
  const programNameIndex: Record<string, string> = {};
  let rowCount = 0;

  // Track (program, day) combos we've seen, so we can verify HH exists
  // for each. Reject if any combo is missing its HH row.
  const seenProgramDay = new Set<string>();

  for (let r = DATA_START_ROW; r < rows.length; r++) {
    const row = rows[r] as unknown[];
    if (!row || row.length === 0) continue;
    const program = String(row[col.program] ?? '').trim();
    const day = String(row[col.day] ?? '').trim();
    const demo = String(row[col.demo] ?? '').trim();
    if (!program || !day || !demo) continue;

    rowCount++;
    seenProgramDay.add(`${program}|${day}`);

    // Universe is per-demo and confirmed consistent across programs.
    const universe = numOrNull(row[col.universe]);
    if (universe != null && universe > 0) {
      universeData[demo] = universe;
    }

    const vpvh = numOrNull(row[col.vpvh]);

    // Map program through ESTIMATE_NAME_MAP if needed (mirrors legacy
    // getEstimate lookup behavior). Also register both the original and
    // mapped form in the case-insensitive index so SF data with minor
    // casing/whitespace drift still resolves at lookup time.
    const mappedProgram = ESTIMATE_NAME_MAP[program] || program;
    programNameIndex[normalizeProgramName(program)] = mappedProgram;
    programNameIndex[normalizeProgramName(mappedProgram)] = mappedProgram;

    const hh = hhIndex.get(`${program}|${day}`);

    for (let q = 0; q < quarterCount; q++) {
      const ql = quarters[q];
      const demoImps = numOrNull(row[col.seImpsStart + q]);
      const demoRtg = numOrNull(row[col.seRtgStart + q]);
      const projImps = numOrNull(row[col.projImpsStart + q]);
      const projRtg = numOrNull(row[col.projRtgStart + q]);

      const key = `${mappedProgram}|${day}|${ql.label}|${demo}RTG`;
      estimateDb[key] = buildEntry({
        demoRtg, demoImps, vpvh,
        hhRtg: hh ? hh.rtgs[q] : null,
        hhImps: hh ? hh.imps[q] : null,
      });

      projectedEstimateDb[key] = buildEntry({
        demoRtg: projRtg, demoImps: projImps, vpvh,
        hhRtg: hh ? hh.projRtgs[q] : null,
        hhImps: hh ? hh.projImps[q] : null,
      });
    }
  }

  // ─── Final HH-coverage check ──────────────────────────────────────────
  const missingHH: string[] = [];
  // forEach over Set is downlevel-safe in es5 target (avoids the iterator
  // requirement of for…of).
  seenProgramDay.forEach((pd) => {
    if (!hhIndex.has(pd)) missingHH.push(pd);
  });
  if (missingHH.length) {
    errors.push(
      `Missing HH demo row for ${missingHH.length} program/day combo(s): ` +
      missingHH.slice(0, 8).join(', ') +
      (missingHH.length > 8 ? `, …(+${missingHH.length - 8} more)` : ''),
    );
  }

  if (errors.length) return { ok: false, errors };

  const primaryYear = quarters[0].year;
  return {
    ok: true,
    version: {
      asOfDate,
      fileName,
      rowCount,
      quarters,
      primaryYear,
      // The xlsx parser is the upfront flow; scatter uploads come through
      // a different route (one-off script + future Upload-tab Type radio).
      // Defaults here keep existing behavior; the Upload tab overrides both
      // from its Type and Rate Card fields. Neither is derivable from the
      // file itself — a WPP workbook is structurally identical to the
      // general master, only the numbers differ.
      kind: 'upfront',
      rateCard: 'general',
      estimateDb,
      projectedEstimateDb,
      universeData,
      programNameIndex,
    },
  };
}

// Re-export for the upload UI / storage layer.
export type { EstimateVersion, ParseResult } from './types';
